import {
  FilterNode,
  GroupNode,
  GroupType,
  NodePath,
  SearchNode,
} from "./advancedSearch.types";

const isGroup = (node: SearchNode): node is GroupNode =>
  node.nodeType === "Group";

export function getNodeAtPath(
  nodes: SearchNode[],
  path: NodePath
): SearchNode | null {
  let level = nodes;
  let node: SearchNode | null = null;

  for (const index of path) {
    node = level[index] ?? null;
    if (!node) {
      return null;
    }
    level = isGroup(node) ? node.children : [];
  }

  return node;
}

/** Applies `change` to the list of siblings the path points into. */
function changeLevel(
  nodes: SearchNode[],
  parent: NodePath,
  change: (siblings: SearchNode[]) => SearchNode[]
): SearchNode[] {
  if (parent.length === 0) {
    return change(nodes);
  }

  const [index, ...rest] = parent;

  return nodes.map((node, position) =>
    position === index && isGroup(node)
      ? { ...node, children: changeLevel(node.children, rest, change) }
      : node
  );
}

export function addNode(
  nodes: SearchNode[],
  node: SearchNode,
  parent: NodePath = []
) {
  return changeLevel(nodes, parent, (siblings) => [...siblings, node]);
}

export function updateNode(
  nodes: SearchNode[],
  path: NodePath,
  node: SearchNode
) {
  return changeLevel(nodes, path.slice(0, -1), (siblings) =>
    siblings.map((sibling, position) =>
      position === path[path.length - 1] ? node : sibling
    )
  );
}

/** A group left with one rule is dissolved into it, and an empty one is dropped. */
function tidy(nodes: SearchNode[]): SearchNode[] {
  return nodes.flatMap((node): SearchNode[] => {
    if (!isGroup(node)) {
      return [node];
    }

    const children = tidy(node.children);

    if (children.length === 0) {
      return [];
    }

    return children.length === 1 && !isGroup(children[0])
      ? [children[0]]
      : [{ ...node, children }];
  });
}

export function removeNode(nodes: SearchNode[], path: NodePath) {
  return tidy(
    changeLevel(nodes, path.slice(0, -1), (siblings) =>
      siblings.filter((_, position) => position !== path[path.length - 1])
    )
  );
}

const sameParent = (paths: NodePath[]) =>
  paths.every(
    (path) =>
      path.length === paths[0].length &&
      path.slice(0, -1).every((step, i) => step === paths[0][i])
  );

/** Whether the rules at these paths can be grouped: two or more, all in one group. */
export function canGroup(paths: NodePath[]) {
  return paths.length >= 2 && sameParent(paths);
}

/** Wraps the nodes at `paths` in a new group, placed where the first of them was. */
export function groupNodes(
  nodes: SearchNode[],
  paths: NodePath[],
  groupType: GroupType
) {
  if (!canGroup(paths)) {
    return nodes;
  }

  const indices = paths.map((path) => path[path.length - 1]).sort((a, b) => a - b);

  return changeLevel(nodes, paths[0].slice(0, -1), (siblings) => {
    const grouped = indices.map((index) => siblings[index]);
    const remaining = siblings.filter((_, index) => !indices.includes(index));
    const group: GroupNode = { nodeType: "Group", groupType, children: grouped };

    remaining.splice(indices[0], 0, group);

    return remaining;
  });
}

/** Replaces a group with its children, in place. */
export function ungroupNode(nodes: SearchNode[], path: NodePath) {
  const node = getNodeAtPath(nodes, path);

  if (!node || !isGroup(node)) {
    return nodes;
  }

  return changeLevel(nodes, path.slice(0, -1), (siblings) =>
    siblings.flatMap((sibling, position) =>
      position === path[path.length - 1] ? node.children : [sibling]
    )
  );
}

export const isFilter = (node: SearchNode): node is FilterNode =>
  node.nodeType === "Filter";
